import apiClient from '../utils/api';

export interface OrderCustomer {
  name: string;
  phone: string;
  email?: string;
}

export interface OrderEvent {
  date: string;
  location: string;
}

export interface OrderOrdered {
  mainService: string;
  details: string;
  extraServices: number;
}

export interface ApiOrder {
  id: number;
  dateCreated: string;
  customer: OrderCustomer;
  event: OrderEvent;
  ordered: OrderOrdered;
  total: number;
  currency: string;
  status: string;
  /**
   * Real event start datetime, present on the raw OrderRecords that
   * `GET /vendors/{vendorId}/orders` returns. `eventDetails.eventDate` is
   * date-only, so this is the only field that carries an event *time*.
   */
  startDateTime?: string;
  eventDetails?: {
    eventDate?: string;
    eventName?: string;
  };
}

export interface OrdersApiResponse {
  success: boolean;
  data: ApiOrder[];
  count: number;
}

// Order Detail interfaces matching backend VendorOrderDetails
export interface OrderDetailCustomer {
  userId: string;
  name: string;
  phone: string;
  email?: string;
}

export interface OrderDetailEvent {
  eventId: string;
  eventName?: string;
  eventDate?: string;
  eventTime?: string;
  eventAddress?: string;
  eventZone?: string;
  eventBuildingNumber?: string;
}

export interface OrderDetailPackage {
  packageId: string;
  productId: string;
  type: string;
  title: string;
  subTitle?: string;
  options?: Array<{
    id: string;
    label: string;
    price?: number;
  }>;
  items?: Array<{
    itemId: string;
    label: string;
    price?: number;
  }>;
  selectedOptions?: Array<{
    id: string;
    label: string;
  }>;
  selectedItems?: Array<{
    itemId: string;
    label: string;
    units: number;
  }>;
}

export interface OrderDetailResponse {
  success: boolean;
  data: {
    orderId: string;
    customer: OrderDetailCustomer;
    event: OrderDetailEvent | null;
    contactDetails?: {
      contactName?: string;
      phoneNumber?: string;
    };
    productName?: string;
    packages?: OrderDetailPackage[];
    specialRequests?: string;
    ordered: string | number;
    totalAmount: number;
    status: string;
  };
}

export interface UpdateOrderStatusResponse {
  success: boolean;
  data: {
    orderId: string;
    status: string;
  };
  message?: string;
}

// Get all orders for a vendor
export const getOrders = async (vendorId: string, status?: string, orderId?: string): Promise<OrdersApiResponse> => {
  let url = `/vendors/${vendorId}/orders`;
  const params: string[] = [];
  if (status) {
    params.push(`status=${encodeURIComponent(status)}`);
  }
  if (orderId) {
    params.push(`orderId=${encodeURIComponent(orderId)}`);
  }
  if (params.length > 0) {
    url += `?${params.join('&')}`;
  }
  const response = await apiClient.get<OrdersApiResponse>(url);
  return response.data;
};

// Get orders with status filter
export const getOrdersByStatus = async (vendorId: string, status: string): Promise<OrdersApiResponse> => {
  const response = await apiClient.get<OrdersApiResponse>(`/vendors/${vendorId}/orders?status=${status}`);
  return response.data;
};

// Get single order details by orderId
export const getOrderById = async (orderId: string): Promise<OrderDetailResponse> => {
  const response = await apiClient.get<OrderDetailResponse>(`/vendors/orders/${orderId}`);
  return response.data;
};

// Update order status
export const updateOrderStatus = async (orderId: string, status: string): Promise<UpdateOrderStatusResponse> => {
  const response = await apiClient.put<UpdateOrderStatusResponse>(`/vendors/orders/${orderId}/status`, { status });
  return response.data;
};

